import React, { useState } from 'react';
import { toast } from 'sonner';

import { sendEmailSignInLink } from '../../services/appwrite';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * Email sign-in: ask for an address, send a one-time link, then say where to
 * look. Following the link lands on /auth/callback, which signs them in (and
 * creates the account if this address has none).
 */
const EmailSignInForm = ({ defaultEmail = '', onBack, submitLabel = 'Email me a sign-in link' }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const send = async (address) => {
    try {
      setSending(true);
      await sendEmailSignInLink(address);
      setSentTo(address.trim().toLowerCase());
    } catch (err) {
      console.error('Could not send sign-in link:', err);
      toast.error(err?.type === 'general_rate_limit_exceeded'
        ? 'Too many sign-in links requested. Please wait a few minutes and try again.'
        : 'We could not send a sign-in link. Please check the address and try again.');
    } finally {
      setSending(false);
    }
  };

  if (sentTo) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm">
          We've sent a sign-in link to <span className="font-medium">{sentTo}</span>.
        </p>
        <p className="text-sm text-muted-foreground">
          Open it on this device to sign in. The link can only be used once.
        </p>
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={() => send(sentTo)} disabled={sending}>
            {sending ? 'Sending…' : 'Send it again'}
          </Button>
          {onBack && (
            <Button variant="ghost" onClick={onBack} disabled={sending}>
              Use a different way to sign in
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (email.trim()) send(email);
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="sign-in-email">Email</Label>
        <Input
          id="sign-in-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourshop.com"
          autoComplete="email"
          autoFocus
          required
          disabled={sending}
          className="h-12 text-base"
        />
      </div>
      <Button type="submit" className="w-full h-12 text-base" disabled={sending || !email.trim()}>
        {sending ? 'Sending…' : submitLabel}
      </Button>
      {onBack && (
        <Button type="button" variant="ghost" className="w-full" onClick={onBack} disabled={sending}>
          Back
        </Button>
      )}
    </form>
  );
};

export default EmailSignInForm;
